import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL, APP_ORIGIN } from '../lib/appUrl';
import { ArrowLeft, Check, Sparkles, Zap, Rocket, Crown } from 'lucide-react';
import { toast } from 'sonner';

const PLANS = [
  {
    id: 'mini',
    name: 'Mini',
    icon: Zap,
    description: 'For solo founders just shipping their first product.',
    monthly: 9,
    annual: 7, // billed annually => $84/yr (~22% off)
    annualTotal: 84,
    features: [
      '80 AI analyses per month',
      'Unlimited workspaces',
      '20 data exports / month',
      'Build / Fix / Ignore lanes',
      'Quick wins & ROI insights',
      'Email support',
    ],
    cta: 'Start with Mini',
    accent: 'border-border',
    badge: null,
  },
  {
    id: 'basic',
    name: 'Basic',
    icon: Rocket,
    description: 'For indie hackers running multiple products at once.',
    monthly: 24,
    annual: 19,
    annualTotal: 228,
    features: [
      '250 AI analyses per month',
      'Unlimited workspaces',
      '50 data exports / month',
      'Everything in Mini',
      'Priority AI categorization',
      'Quick wins highlighted',
      'Priority email support',
    ],
    cta: 'Start with Basic',
    accent: 'border-primary ring-2 ring-primary/30 shadow-lg',
    badge: 'Most popular',
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Crown,
    description: 'For agencies and serious operators managing client portfolios.',
    monthly: 59,
    annual: 47,
    annualTotal: 564,
    features: [
      '600 AI analyses per month',
      'Unlimited workspaces',
      'Unlimited data exports',
      'Everything in Basic',
      'Advanced revenue forecasting',
      'Custom segment filters',
      'Priority support (24h SLA)',
      'Early access to new features',
    ],
    cta: 'Start with Pro',
    accent: 'border-border',
    badge: null,
  },
];

const FAQS = [
  {
    q: 'What counts as an AI analysis?',
    a: "One 'analysis' is a single batch of feedback that the AI processes — regardless of how many signals are inside. So a batch with 50 tweets counts as 1 analysis, not 50. Signals can be added manually or via automated Signal Sources (Reddit, HN, RSS).",
  },
  {
    q: 'Can I switch plans anytime?',
    a: 'Yes. Upgrade or downgrade at any time — changes take effect on your next billing cycle. No long-term contracts.',
  },
  {
    q: 'Do I get a free trial?',
    a: 'The free plan includes 10 analyses/day with no card required. Paid checkout is not yet available; monthly quotas apply once you upgrade.',
  },
  {
    q: 'What happens if I hit my monthly limit?',
    a: "You'll see an in-app prompt to upgrade. Existing data stays accessible — you just can't run new analyses until you upgrade or your quota resets on the 1st.",
  },
  {
    q: 'Is there a refund policy?',
    a: '14-day money-back guarantee on all annual plans. Monthly plans can be cancelled anytime; no refunds on the current billing month.',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(true);

  const handleSelectPlan = (planId) => {
    toast.info('Coming soon', {
      description: `Payments aren't enabled in this preview. We'll notify you when ${planId.toUpperCase()} goes live.`,
      duration: 5000,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b bg-card sticky top-0 z-50">
        <div className="container-wide mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="pricing-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold">Pricing</h1>
            <Badge variant="secondary" className="text-xs">Soft launch</Badge>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            data-testid="pricing-dashboard-link"
          >
            <a href={APP_DASHBOARD_URL}>Open dashboard</a>
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 sm:py-20 border-b">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Badge
            variant="outline"
            className="mb-4 border-primary/30 text-primary"
            data-testid="pricing-hero-badge"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Built for founders who ship
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Stop guessing what to build next.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Pick a plan that matches how much feedback you actually wrangle. Upgrade, downgrade, or cancel anytime — no contracts.
          </p>

          {/* Billing toggle */}
          <div
            className="inline-flex items-center gap-3 p-1.5 bg-card border rounded-full"
            data-testid="pricing-billing-toggle"
          >
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !annual ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="pricing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                annual ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="pricing-toggle-annual"
            >
              Annual
              <Badge
                variant="secondary"
                className={`text-[10px] px-1.5 py-0 ${annual ? 'bg-background text-foreground' : ''}`}
              >
                Save 20%
              </Badge>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const price = annual ? plan.annual : plan.monthly;
              return (
                <Card
                  key={plan.id}
                  className={`p-6 flex flex-col relative transition-shadow hover:shadow-md ${plan.accent}`}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {plan.badge && (
                    <Badge
                      className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground"
                      data-testid={`pricing-badge-${plan.id}`}
                    >
                      {plan.badge}
                    </Badge>
                  )}

                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6 min-h-[40px]">{plan.description}</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold tabular-nums" data-testid={`pricing-price-${plan.id}`}>
                        ${price}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {annual
                        ? `Billed annually — $${plan.annualTotal}/year`
                        : 'Billed monthly'}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={plan.badge ? 'btn-cta w-full' : 'w-full'}
                    variant={plan.badge ? 'default' : 'outline'}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    {plan.cta}
                  </Button>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Button
              asChild
              className="btn-cta"
              data-testid="pricing-get-started"
            >
              <a href={APP_ORIGIN}>Get started free</a>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <Card key={i} className="p-5" data-testid={`pricing-faq-${i}`}>
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button
              variant="outline"
              onClick={() => navigate('/faq')}
              data-testid="pricing-see-all-faqs"
            >
              See all FAQs
            </Button>
          </div>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default PricingPage;
